import { create } from 'zustand';
import { useConnectionStore } from '@/stores/connectionStore';
import { useBrowserStore } from '@/stores/browserStore';
import { useChatStore } from '@/stores/chatStore';

// Narration bubbles for the write phase — mirrors the read-setup narration
// (idempotent via `once` so reloads / retries don't stack duplicates).
function narrate(content: string) {
  void useChatStore.getState().addAssistantMessage(content, undefined, undefined, { once: true });
}
const CORE_SCOPES_NARRATED = ['Change account meeting settings', 'Update user settings', 'Update meetings'];
/** What the enablement is about to add: the core set, or the scopes a plan's API steps need (D7), by name. */
function opening(scopes: string[] | undefined): string {
  const list = (scopes && scopes.length > 0 ? scopes : CORE_SCOPES_NARRATED).map((scope) => `• ${scope}`).join('\n');
  const why = scopes && scopes.length > 0 ? 'the plan needs permissions your Zoom app does not have yet' : 'to apply that change I need permission to change your Zoom settings';
  return `Enabling write access — ${why}. I'm adding these scopes to your app now (watch the browser):\n\n${list}`;
}

/**
 * Write-access enablement, run as a guided phase inside the Zoom setup chat.
 *
 * A plan queues it: one whose card said the tenant is read-only, a report fix
 * that needs write access, or one whose API steps need scopes the app lacks
 * ("Add scopes", D3, D7). The plan is stashed here and the user redirected to
 * the setup chat; this store drives the visible enablement (sign-in if needed →
 * add the scopes → activate → verify) with the browser panel open, then hands
 * control back to the plan's page to approve it.
 *
 * It is mirrored to sessionStorage so it survives a reload mid-enablement
 * (design: persist across reload; silently dropped on abandon since
 * sessionStorage clears with the tab / on explicit reset).
 */
const PENDING_PLAN_KEY = 'zac_pending_write_plan';

/** A plan held for write access, to open once it is on; with the scopes its API steps still need ("Add scopes"). */
export interface PendingPlan {
  planId: string;
  conversationId?: string;
  scopes?: string[];
}

export type WritePhase = 'idle' | 'signin' | 'scopes' | 'done' | 'error';

interface WriteEnableState {
  /** The plan queued to open once write is enabled. */
  pendingPlan: PendingPlan | null;
  phase: WritePhase;
  /** Zoom sign-in required in the harness before scopes can be added. */
  signinNeeded: boolean;
  error: string | null;

  setPendingPlan: (plan: PendingPlan) => void;
  clearPendingPlan: () => void;
  /** Run (or retry) the enablement. Advances phase; opens no UI itself. */
  run: () => Promise<void>;
  /** Reset transient phase state (not the pending plan). */
  reset: () => void;
}

function readPendingPlan(): PendingPlan | null {
  try {
    const raw = sessionStorage.getItem(PENDING_PLAN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingPlan>;
    if (typeof parsed.planId !== 'string' || parsed.planId === '') return null;
    const scopes = Array.isArray(parsed.scopes) ? parsed.scopes.filter((scope): scope is string => typeof scope === 'string') : [];
    return {
      planId: parsed.planId,
      ...(typeof parsed.conversationId === 'string' ? { conversationId: parsed.conversationId } : {}),
      ...(scopes.length > 0 ? { scopes } : {}),
    };
  } catch {
    return null;
  }
}

export const useWriteEnableStore = create<WriteEnableState>((set, get) => ({
  pendingPlan: readPendingPlan(),
  phase: 'idle',
  signinNeeded: false,
  error: null,

  // One pending plan at a time: whatever queued the enablement last is what it hands back to.
  setPendingPlan: (plan) => {
    try { sessionStorage.setItem(PENDING_PLAN_KEY, JSON.stringify(plan)); } catch { /* private mode */ }
    set({ pendingPlan: plan, phase: 'idle', signinNeeded: false, error: null });
  },

  clearPendingPlan: () => {
    try { sessionStorage.removeItem(PENDING_PLAN_KEY); } catch { /* ignore */ }
    set({ pendingPlan: null });
  },

  run: async () => {
    if (get().phase === 'scopes') return; // already running
    set({ phase: 'scopes', signinNeeded: false, error: null });
    const scopes = get().pendingPlan?.scopes;
    narrate(opening(scopes));
    // ZAC is driving the browser to add scopes — show the "ZAC is controlling"
    // blue overlay on the panel for the duration.
    useBrowserStore.getState().setAgentControl(true);
    const res = await useConnectionStore.getState().enableWrite(scopes, get().pendingPlan?.planId);
    if (res.enabled && res.missing !== undefined) {
      // The app could not be given every scope (live 2026-11-03: a classic scope a granular app's page does not offer,
      // asked for three times): said as it is, never as done.
      useBrowserStore.getState().setAgentControl(false);
      narrate(`Zoom didn't give your app ${res.missing.length === 1 ? 'this scope' : 'these scopes'}: ${res.missing.join(', ')}. Its Scopes page doesn't offer ${res.missing.length === 1 ? 'it' : 'them'}, so trying again won't help; tell me in the plan's chat and I'll find another way.`);
      set({ phase: 'error', error: `Zoom didn't grant ${res.missing.join(', ')}` });
      return;
    }
    if (res.enabled) {
      useBrowserStore.getState().setAgentControl(false);
      const { pendingPlan } = get();
      narrate(
        (scopes && scopes.length > 0 ? 'Added the scopes, activated the app, and verified them. ✅ ' : 'Added the write scopes, activated the app, and verified write access. ✅ ') +
          (pendingPlan
            ? (scopes && scopes.length > 0 ? 'Taking you back to your plan.' : 'Write access is on — taking you to your plan to approve it.')
            : 'Write access is on.'),
      );
      set({ phase: 'done', signinNeeded: false, error: null });
      return;
    }
    if (res.error === 'harness_session_required') {
      // Needs a signed-in Zoom session first — hand the browser back to the user.
      useBrowserStore.getState().setAgentControl(false);
      narrate("I can't reach Zoom in the browser panel yet — opening it. Sign in to Zoom there if it asks, and I'll continue on my own as soon as it's signed in.");
      // Signed in is read again from the tab (WriteEnablePanel): a stale true would retry at once.
      useBrowserStore.getState().setZoomSignedIn(false);
      set({ phase: 'signin', signinNeeded: true, error: res.message ?? 'Sign in to Zoom to continue.' });
      return;
    }
    useBrowserStore.getState().setAgentControl(false);
    void useChatStore.getState().addAssistantMessage(
      `I couldn't enable write access: ${res.message ?? 'unknown error'}. Tell me to retry, or hit Retry above.`,
    );
    set({ phase: 'error', error: res.message ?? 'Could not enable write access — try again.' });
  },

  reset: () => {
    useBrowserStore.getState().setAgentControl(false);
    set({ phase: 'idle', signinNeeded: false, error: null });
  },
}));
